import React from "react";
import get from "lodash/get";
import { colors } from "app/theme";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useAuth0 } from "@auth0/auth0-react";
import Collapse from "@mui/material/Collapse";
import Container from "@mui/material/Container";
import { Link, useLocation } from "react-router-dom";
import { useStoreState } from "app/state/store/hooks";
import { SxProps, useMediaQuery } from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import PlatformWalkthroughTooltip from "app/components/platform-walkthrough-tooltip";
import { HeaderButtonsProps } from "app/components/header/sub-components/buttons/data";
import { HeaderSignInView } from "app/components/header/sub-components/expanded-view/views/signin";
import { HeaderExplorerView } from "app/components/header/sub-components/expanded-view/views/explorer";
import { HeaderPublisherView } from "app/components/header/sub-components/expanded-view/views/publisher";
import { HeaderResourcesView } from "app/components/header/sub-components/expanded-view/views/resources";

export const HeaderButtons: React.FC<HeaderButtonsProps> = (
  props: HeaderButtonsProps
) => {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth0();
  const mobile = useMediaQuery("(max-width: 743px)");

  const storedUser = useStoreState((state) => get(state.user, "data.data", {}));

  const getView = (key: string) => {
    switch (key) {
      case props.buttons[0].key:
        return <HeaderExplorerView />;
      case props.buttons[1].key:
        return <HeaderPublisherView />;
      case props.buttons[2].key:
        return <HeaderResourcesView />;
      case props.buttons[4].key:
        return <HeaderSignInView />;
      default:
        return null;
    }
  };

  if (mobile) {
    return (
      <Box
        sx={{
          background: colors.secondary.lightGrey,
          borderBottom: `1px solid ${colors.secondary.iconGray}`,
        }}
      >
        <Container
          maxWidth="lg"
          disableGutters
          sx={{
            padding: "0 24px !important",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
              button: {
                height: "32px",
                fontSize: "14px",
                fontWeight: "700",
                borderRadius: "5px",
                border: "1px solid transparent",
              },
            }}
          >
            {props.buttons.map((button) => {
              let buttonStyle: SxProps =
                button.key === props.activeButton
                  ? {
                      borderColor: colors.secondary.iconGray + "!important",
                      background: colors.secondary.disableButton,
                    }
                  : {};
              buttonStyle = {
                ...buttonStyle,
                color: button.color ?? colors.text.title,
                ".MuiButton-endIcon": {
                  marginLeft: "2px !important",
                },
              };
              if (button.link) {
                return (
                  <Button
                    key={button.key}
                    component={Link}
                    to={button.link}
                    sx={buttonStyle}
                  >
                    {button.label}
                  </Button>
                );
              }
              let icon;
              if (button.expandable) {
                icon =
                  button.key === props.activeButton ? (
                    <ExpandLess />
                  ) : (
                    <ExpandMore />
                  );
              }
              return (
                <Box
                  key={button.key}
                  sx={{
                    width: "100%",
                    padding: "5px 0",
                    "&:not(:last-child)": {
                      borderBottom: `0.5px solid ${colors.secondary.iconGray}`,
                    },
                  }}
                >
                  <Button
                    key={button.key}
                    endIcon={icon}
                    onClick={() => props.handleButtonClick(button.key)}
                    sx={buttonStyle}
                    startIcon={
                      button.signIn && isAuthenticated ? (
                        <img
                          src={user?.picture}
                          width={20}
                          height={20}
                          style={{ borderRadius: "50%" }}
                          alt="user-avatar"
                        />
                      ) : undefined
                    }
                  >
                    {button.signIn && isAuthenticated
                      ? `Hello, ${get(storedUser, "name", "-").split(" ")[0]}`
                      : button.label}
                  </Button>
                  <Collapse in={button.key === props.activeButton}>
                    <Box
                      sx={{
                        paddingTop: "20px",
                        paddingBottom: "5px",
                      }}
                    >
                      {getView(button.key)}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        </Container>
      </Box>
    );
  }

  const HeaderButton = ({
    button,
  }: {
    button: HeaderButtonsProps["buttons"][number];
  }) => {
    let buttonStyle: SxProps =
      button.key === props.activeButton
        ? {
            background: colors.secondary.disableButton,
            borderColor: colors.secondary.iconGray + "!important",
          }
        : {};
    if (!button.expandable && button.link === location.pathname) {
      buttonStyle = {
        background: colors.secondary.disableButton,
        borderColor: colors.secondary.iconGray + "!important",
      };
    }
    buttonStyle = {
      ...buttonStyle,
      color: button.color ?? colors.text.title,
      ".MuiButton-endIcon": {
        marginLeft: "2px !important",
      },
    };
    if (button.link) {
      return (
        <Button
          key={button.key}
          component={Link}
          to={button.link}
          sx={buttonStyle}
        >
          {button.label}
        </Button>
      );
    }
    let icon;
    if (button.expandable) {
      icon =
        button.key === props.activeButton ? <ExpandLess /> : <ExpandMore />;
    }
    return (
      <Button
        key={button.key}
        endIcon={icon}
        onClick={() => props.handleButtonClick(button.key)}
        sx={buttonStyle}
        startIcon={
          button.signIn && isAuthenticated ? (
            <img
              src={user?.picture}
              width={20}
              height={20}
              style={{ borderRadius: "50%" }}
              alt="user-avatar"
            />
          ) : undefined
        }
        data-cy="navbar-button"
      >
        {button.signIn && isAuthenticated
          ? `Hello, ${get(storedUser, "name", "-").split(" ")[0]}`
          : button.label}
      </Button>
    );
  };

  return (
    <Box
      sx={{
        gap: "5px",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        "& > button, a": {
          height: "32px",
          fontSize: "14px",
          fontWeight: "700",
          borderRadius: "5px",
          border: "1px solid transparent",
        },
      }}
    >
      {props.buttons.map((button) => {
        return (
          <PlatformWalkthroughTooltip
            key={button.key}
            step={
              button.key === "explorer"
                ? 1
                : button.key === "publisher"
                ? 2
                : -2
            }
          >
            <HeaderButton button={button} />
          </PlatformWalkthroughTooltip>
        );
      })}
    </Box>
  );
};
